import type { LinkAnswer, LinkGist, UrlEntity } from '../domain/noteTypes'
import { fetchAnswer, fetchGist, fetchReadable, gistsEnabled, type AnswerFetchResult, type GistFetchResult, type ReadableFetchResult } from './gistClient'
import { GIST_SETTINGS, decideGistRead, type GistContext, type GistSettings, type GistTrigger } from './gistPolicy'
import { linkMetadataUrlKey } from './linkMetadataWork'
import { linkPreviewPresentation } from './linkPreviewCategory'

/**
 * Runs the gist flow for a link the person is looking at (docs/link-notes/GIST.md): ask the policy, reuse a gist
 * another note already has, otherwise ask the Worker, then save the result on the link. A gist is read only on
 * attention (a card on screen, a peek, an expanded row) or when asked for; failures are quiet.
 */
/**
 * What the UI may say about a link's read. `unreadable`, `failed`, `limit` and `signed-out` are only recorded for a read the
 * person asked for (so they are told why nothing happened); a read the app started itself ends in the quiet `no-gist`.
 */
/** Whether a page can be read for a summary or an answer: unknown until a read or a check has told us. */
export type Readability = { state: 'unknown' | 'checking' | 'readable' } | { state: 'blocked'; reason: string }

export type GistUiStatus = 'reading' | 'unreadable' | 'failed' | 'limit' | 'signed-out' | 'no-gist'

export interface GistControllerDeps {
  /** Every url entity, from every note, that has this link key. */
  linksWithKey: (key: string) => UrlEntity[]
  /** Writes (or clears) the gist on every note's copy of the link and refreshes the UI. */
  saveGist: (key: string, gist: LinkGist | null) => Promise<void>
  /** Adds an answer to every note's copy of the link (newest first, the oldest dropped past the limit). */
  saveAnswer?: (key: string, answer: LinkAnswer) => Promise<void>
  /** Removes one answer from every note's copy of the link. */
  removeAnswer?: (key: string, id: string) => Promise<void>
  fetchAnswer?: (url: string, question: string) => Promise<AnswerFetchResult>
  fetchReadable?: (url: string) => Promise<ReadableFetchResult>
  /** Called whenever a status changes, so the UI can redraw. */
  changed: () => void
  fetchGist?: (url: string, options: { refresh?: boolean }) => Promise<GistFetchResult>
  enabled?: () => boolean
  now?: () => number
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
  online?: () => boolean
  metered?: () => boolean
  settings?: GistSettings
}

/** How long a refusal is remembered on this device. The Worker remembers a blocked site for the same day. */
const BLOCKED_TTL_MS = 24 * 3_600_000
const BLOCKED_KEY = 'minimany:gist-blocked:v1'

const dayKey = (now: number) => `minimany:gist-reads:${new Date(now).toISOString().slice(0, 10)}`

export function createGistController(deps: GistControllerDeps) {
  const settings = deps.settings ?? GIST_SETTINGS
  const now = deps.now ?? Date.now
  const statuses = new Map<string, GistUiStatus>()
  const attempts = new Map<string, { count: number; last: number }>()
  const inflight = new Set<string>()
  // Questions have their own status: asking is always the person's own request, so it is never quiet.
  const askStatuses = new Map<string, GistUiStatus>()
  // The technical reason behind a failure, kept so the person (and the owner testing) can see what happened.
  const details = new Map<string, string>()
  const detail = (key: string, reason: string | null) => { if (reason) details.set(key, reason); else details.delete(key) }
  const asking = new Set<string>()
  // What is known about whether each page can be read, from any read, question or check this session.
  const readability = new Map<string, Readability>()
  // A site that refuses to be read is remembered (a day), so changing the layout, reopening the note or restarting the app never
  // asks it again.
  const blockedStore = (): Record<string, { reason: string; at: number }> => {
    try { return JSON.parse(storage?.getItem(BLOCKED_KEY) ?? '{}') as Record<string, { reason: string; at: number }> } catch { return {} }
  }
  const note = (key: string, value: Readability) => {
    readability.set(key, value)
    try {
      const store = blockedStore()
      let changed = false
      if (value.state === 'blocked') { store[key] = { reason: value.reason, at: now() }; changed = true }
      else if (value.state === 'readable' && key in store) { delete store[key]; changed = true }
      for (const [stored, entry] of Object.entries(store)) if (now() - entry.at > BLOCKED_TTL_MS) { delete store[stored]; changed = true }
      if (changed) storage?.setItem(BLOCKED_KEY, JSON.stringify(store))
    } catch { /* A private window: it simply asks again. */ }
  }
  const known = (key: string): Readability | undefined => {
    const held = readability.get(key)
    if (held) return held
    const stored = blockedStore()[key]
    if (stored && now() - stored.at <= BLOCKED_TTL_MS) { const value: Readability = { state: 'blocked', reason: stored.reason }; readability.set(key, value); return value }
    return undefined
  }
  // Once the server says the day or the month is spent, stop asking until it resets.
  let pausedUntil = 0
  const storage = deps.storage === undefined ? (typeof localStorage === 'undefined' ? null : localStorage) : deps.storage

  const readsToday = (): number => {
    try { return Number(storage?.getItem(dayKey(now())) ?? 0) || 0 } catch { return 0 }
  }
  const countRead = () => { try { storage?.setItem(dayKey(now()), String(readsToday() + 1)) } catch { /* A private window: the server still bounds spend. */ } }
  const setStatus = (key: string, status: GistUiStatus | null) => {
    if (status) statuses.set(key, status); else statuses.delete(key)
    deps.changed()
  }

  async function request(url: UrlEntity, trigger: GistTrigger, options: { refresh?: boolean; linksInNote?: number } = {}): Promise<void> {
    if (!(deps.enabled ?? gistsEnabled)()) return
    const key = linkMetadataUrlKey(url)
    if (inflight.has(key)) return
    if (known(key)?.state === 'blocked') { if (trigger === 'manual') { detail(key, 'blocked'); setStatus(key, 'unreadable') } return }
    if (now() < pausedUntil) { if (trigger === 'manual') setStatus(key, 'limit'); return }
    const copies = deps.linksWithKey(key)
    const sibling = copies.find((copy) => copy !== url && copy.gist && !options.refresh)
    const category = linkPreviewPresentation(url).category
    const tried = attempts.get(key)
    const context: GistContext = {
      trigger,
      origin: 'paste',
      pageKind: category === 'link' ? null : category,
      domain: url.domain,
      hasGist: Boolean(url.gist) && !options.refresh,
      cached: Boolean(sibling),
      metadataReady: url.fetchedAt !== null,
      linksInNote: options.linksInNote ?? 1,
      online: deps.online ? deps.online() : typeof navigator === 'undefined' || navigator.onLine !== false,
      metered: deps.metered ? deps.metered() : Boolean((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData),
      attemptCount: options.refresh ? 0 : tried?.count ?? 0,
      lastAttemptAt: options.refresh ? null : tried?.last ?? null,
      readsToday: readsToday(),
      now: now(),
    }
    const asked = trigger === 'manual'
    const decision = decideGistRead(context, { ...settings, enabled: true })
    if (decision.action === 'skip') {
      // A person who asked is told why nothing happened; an automatic read just does not start.
      if (asked) { detail(key, !context.online ? 'offline' : decision.rule); setStatus(key, !context.online ? 'failed' : decision.rule === 'limit:budget' ? 'limit' : 'failed') }
      return
    }
    if (decision.action === 'reuse' && sibling?.gist) { await deps.saveGist(key, sibling.gist); return }

    inflight.add(key)
    detail(key, null)
    setStatus(key, 'reading')
    try {
      const result = await (deps.fetchGist ?? fetchGist)(url.originalUrl, { refresh: options.refresh })
      if (result.status === 'ready') {
        note(key, { state: 'readable' })
        countRead()
        attempts.delete(key)
        await deps.saveGist(key, result.gist)
        setStatus(key, null)
      } else if (result.status === 'unreadable') {
        note(key, { state: 'blocked', reason: result.reason })
        attempts.set(key, { count: (tried?.count ?? 0) + 1, last: now() })
        detail(key, result.reason)
        setStatus(key, asked ? 'unreadable' : 'no-gist')
      } else if (result.status === 'failed' && (result.reason === 'gist_daily_limit' || result.reason === 'gist_budget_exhausted')) {
        const next = new Date(now()); next.setUTCHours(24, 0, 0, 0)
        pausedUntil = result.reason === 'gist_daily_limit' ? next.getTime() : now() + 3_600_000
        setStatus(key, asked ? 'limit' : 'no-gist')
      } else {
        if (result.status !== 'signed-out') attempts.set(key, { count: (tried?.count ?? 0) + 1, last: now() })
        detail(key, result.reason)
        setStatus(key, asked ? result.status : 'no-gist')
      }
    } finally {
      inflight.delete(key)
    }
  }

  /** Asks one question about a page. Resolves true when an answer was saved; otherwise the reason is in `askStatusOf`. */
  async function ask(url: UrlEntity, question: string): Promise<boolean> {
    const key = linkMetadataUrlKey(url)
    const text = question.replace(/\s+/g, ' ').trim()
    if (!text || !(deps.enabled ?? gistsEnabled)() || asking.has(key)) return false
    const fail = (status: GistUiStatus, reason: string): false => { askStatuses.set(key, status); detail(`ask:${key}`, reason); deps.changed(); return false }
    if (!(deps.online ? deps.online() : typeof navigator === 'undefined' || navigator.onLine !== false)) return fail('failed', 'offline')
    const refused = known(key)
    if (refused?.state === 'blocked') return fail('unreadable', refused.reason)
    if (now() < pausedUntil || readsToday() >= settings.dailyReadsPerAccount) return fail('limit', 'limit')
    asking.add(key)
    detail(`ask:${key}`, null)
    askStatuses.set(key, 'reading')
    deps.changed()
    try {
      const result = await (deps.fetchAnswer ?? fetchAnswer)(url.originalUrl, text)
      if (result.status === 'unreadable') note(key, { state: 'blocked', reason: result.reason })
      if (result.status !== 'ready') {
        if (result.status === 'failed' && (result.reason === 'gist_daily_limit' || result.reason === 'gist_budget_exhausted')) {
          const next = new Date(now()); next.setUTCHours(24, 0, 0, 0)
          pausedUntil = result.reason === 'gist_daily_limit' ? next.getTime() : now() + 3_600_000
          return fail('limit', result.reason)
        }
        return fail(result.status, result.reason)
      }
      note(key, { state: 'readable' })
      countRead()
      await deps.saveAnswer?.(key, { ...result.answer, id: `${now().toString(36)}${Math.random().toString(36).slice(2, 7)}` })
      askStatuses.delete(key)
      deps.changed()
      return true
    } finally {
      asking.delete(key)
    }
  }

  /** Finds out, once per page, whether it can be read, so a person is told before they write a question. */
  async function check(url: UrlEntity): Promise<void> {
    const key = linkMetadataUrlKey(url)
    if (!(deps.enabled ?? gistsEnabled)() || known(key)) return
    if (url.gist || url.answers?.length) { note(key, { state: 'readable' }); deps.changed(); return }
    if (!(deps.online ? deps.online() : typeof navigator === 'undefined' || navigator.onLine !== false)) return
    note(key, { state: 'checking' })
    deps.changed()
    try {
      const result = await (deps.fetchReadable ?? fetchReadable)(url.originalUrl)
      if (result.status === 'readable') note(key, { state: 'readable' })
      else if (result.status === 'unreadable') note(key, { state: 'blocked', reason: result.reason })
      else readability.delete(key)
    } catch { readability.delete(key) }
    deps.changed()
  }

  return {
    request,
    ask,
    check,
    readabilityOf: (url: UrlEntity): Readability => known(linkMetadataUrlKey(url)) ?? { state: 'unknown' },
    detailOf: (url: UrlEntity): string | null => details.get(linkMetadataUrlKey(url)) ?? null,
    askDetailOf: (url: UrlEntity): string | null => details.get(`ask:${linkMetadataUrlKey(url)}`) ?? null,
    askStatusOf: (url: UrlEntity): GistUiStatus | null => askStatuses.get(linkMetadataUrlKey(url)) ?? null,
    clearAskStatus: (url: UrlEntity) => { if (askStatuses.delete(linkMetadataUrlKey(url))) deps.changed() },
    removeAnswer: (url: UrlEntity, id: string) => deps.removeAnswer?.(linkMetadataUrlKey(url), id) ?? Promise.resolve(),
    statusOf: (url: UrlEntity): GistUiStatus | null => statuses.get(linkMetadataUrlKey(url)) ?? null,
    /** Hide keeps the text, so Refresh (or a new read) can bring a gist back. */
    hide: (url: UrlEntity) => url.gist ? deps.saveGist(linkMetadataUrlKey(url), { ...url.gist, hidden: true }) : Promise.resolve(),
  }
}

export type GistController = ReturnType<typeof createGistController>

/** What a card needs to show and drive its own link's gist. */
export interface LinkGistUi {
  status: GistUiStatus | null
  /** Why this page cannot be read, when that is known; null otherwise. */
  blockedReason: string | null
  /** The technical reason behind the last failed read, shown small so a failure can be understood. */
  statusDetail: string | null
  want: (trigger: GistTrigger, refresh?: boolean) => void
  hide: () => void
}

/** What the full-screen answers view needs: the state of the person's own questions and how to ask, clear and remove one. */
export interface LinkAskUi {
  status: GistUiStatus | null
  statusDetail: string | null
  readability: Readability
  /** Finds out whether the page can be read (once); called when the view opens. */
  check: () => void
  ask: (question: string) => Promise<boolean>
  clearStatus: () => void
  remove: (id: string) => void
}

/** What the editor needs to show and drive the gist of any link in the note. */
export interface EditorGistUi {
  status: (url: UrlEntity) => GistUiStatus | null
  want: (url: UrlEntity, trigger: GistTrigger, refresh?: boolean) => void
  hide: (url: UrlEntity) => void
}
